import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, Search, Eye, Receipt,
  Loader2, Calendar, User, IndianRupee
} from 'lucide-react';
import api from '../../utils/api';

const MobileExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [expenseHeaders, setExpenseHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [headerFilter, setHeaderFilter] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  
  // Get user from localStorage
  const user = React.useMemo(() => {
    try {
      const storedUser = localStorage.getItem('suntech_mobile_user') || localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  }, []);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    header_id: '',
    amount: '',
    description: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_mode: 'Cash'
  });

  const fetchExpenses = useCallback(async () => {
    try {
      const response = await api.get('/expenses/');
      // Sort by date (newest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.expense_date || b.created_at) - new Date(a.expense_date || a.created_at)
      );
      setExpenses(sorted);
    } catch (error) {
      toast.error('Failed to fetch expenses');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchExpenseHeaders = useCallback(async () => {
    try {
      const response = await api.get('/expense-headers/');
      setExpenseHeaders(response.data);
      // Set default header if available
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, header_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch expense headers:', error);
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
    fetchExpenseHeaders();
  }, [fetchExpenses, fetchExpenseHeaders]);

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      expense.description?.toLowerCase().includes(searchLower) ||
      expense.header_name?.toLowerCase().includes(searchLower) ||
      expense.submitted_by_name?.toLowerCase().includes(searchLower);
    
    const matchesHeader = headerFilter === 'all' || expense.header_id === headerFilter;
    
    return matchesSearch && matchesHeader;
  });

  // Calculate totals
  const totalAmount = filteredExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);

  const getHeaderColor = (headerName) => {
    // Generate consistent color based on header name
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-yellow-100 text-yellow-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800',
      'bg-indigo-100 text-indigo-800',
      'bg-cyan-100 text-cyan-800',
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800',
      'bg-red-100 text-red-800',
      'bg-teal-100 text-teal-800',
    ];
    if (!headerName) return 'bg-gray-100 text-gray-800';
    const hash = headerName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const handleCreateExpense = async () => {
    if (!formData.header_id || !formData.amount || !formData.description) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/expenses/', {
        header_id: formData.header_id,
        amount: parseFloat(formData.amount),
        description: formData.description,
        expense_date: formData.expense_date,
        payment_mode: formData.payment_mode
      });
      toast.success('Expense added successfully');
      setCreateDialogOpen(false);
      setFormData({
        header_id: expenseHeaders.length > 0 ? expenseHeaders[0].id : '',
        amount: '',
        description: '',
        expense_date: new Date().toISOString().split('T')[0],
        payment_mode: 'Cash'
      });
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Expenses</h1>
          <p className="text-sm text-gray-500">{filteredExpenses.length} entries</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} size="sm">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
        <CardContent className="p-4">
          <p className="text-sm opacity-90">Total Expenses</p>
          <p className="text-2xl font-bold">₹{totalAmount.toLocaleString('en-IN')}</p>
        </CardContent>
      </Card>

      {/* Search & Filters */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search expenses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={headerFilter} onValueChange={setHeaderFilter}>
          <SelectTrigger>
            <SelectValue placeholder="All Expense Heads" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Expense Heads</SelectItem>
            {expenseHeaders.map(header => (
              <SelectItem key={header.id} value={header.id}>{header.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Expenses List */}
      {filteredExpenses.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No expenses found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredExpenses.map((expense) => (
            <Card key={expense.id} className="overflow-hidden">
              <CardContent className="p-4">
                {/* Header Row */}
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className={getHeaderColor(expense.header_name)}>
                      {expense.header_name || 'Unknown'}
                    </Badge>
                    <Badge variant={expense.status === 'Approved' ? 'default' : expense.status === 'Rejected' ? 'destructive' : 'outline'}>
                      {expense.status || 'Pending'}
                    </Badge>
                  </div>
                  <p className="text-lg font-bold text-red-600">
                    ₹{expense.amount?.toLocaleString('en-IN')}
                  </p>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-700 mb-2 line-clamp-2">{expense.description}</p>

                {/* Details */}
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{expense.expense_date ? new Date(expense.expense_date).toLocaleDateString('en-IN') : 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>{expense.submitted_by_name || 'Unknown'}</span>
                  </div>
                </div>

                {/* Action Button */}
                <div className="mt-3">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full"
                    onClick={() => { setSelectedExpense(expense); setViewDialogOpen(true); }}
                  >
                    <Eye className="w-4 h-4 mr-1" /> View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Expense Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Expense Head *</Label>
              <Select value={formData.header_id} onValueChange={(v) => setFormData(prev => ({ ...prev, header_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select expense head" />
                </SelectTrigger>
                <SelectContent>
                  {expenseHeaders.map(header => (
                    <SelectItem key={header.id} value={header.id}>{header.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Amount (₹) *</Label>
              <Input 
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
              />
            </div>
            
            <div>
              <Label>Date *</Label>
              <Input 
                type="date"
                value={formData.expense_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expense_date: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Payment Mode</Label>
              <Select value={formData.payment_mode} onValueChange={(v) => setFormData(prev => ({ ...prev, payment_mode: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Card">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea 
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the expense..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateExpense} disabled={submitting}>
              {submitting ? 'Adding...' : 'Add Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Expense Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Expense Details</DialogTitle>
          </DialogHeader>
          {selectedExpense && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={getHeaderColor(selectedExpense.header_name)}>{selectedExpense.header_name || 'Unknown'}</Badge>
                <Badge variant={selectedExpense.status === 'Approved' ? 'default' : selectedExpense.status === 'Rejected' ? 'destructive' : 'outline'}>
                  {selectedExpense.status || 'Pending'}
                </Badge>
              </div>
              
              <div className="text-center py-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-500">Amount</p>
                <p className="text-3xl font-bold text-red-600">₹{selectedExpense.amount?.toLocaleString('en-IN')}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium">{selectedExpense.expense_date ? new Date(selectedExpense.expense_date).toLocaleDateString('en-IN') : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Submitted By</p>
                  <p className="font-medium">{selectedExpense.submitted_by_name || 'Unknown'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500">Description</p>
                  <p className="font-medium">{selectedExpense.description}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileExpenses;
